import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../components/lab-dynamic-table";
import { formatCurrency } from "../../../utils/numberUtils";
import DateRangeSelector from "../../../components/DateRangeSelector";

// Collection Center interface
interface CollectionCenter {
  _id: string;
  collectionCenterName: string;
  descripiton: string;
  address: string;
  share?: number | null;
}

// Payment summary data interface
interface PaymentSummaryData {
  _id: string;
  patientCount: number;
  paidAmount: number;
  discount: number;
  dueAmount: number;
  grossAmount: number;
  collectionCenter: CollectionCenter;
}

const getCenterShare = (center?: CollectionCenter | null) => {
  if (center?.share === null || center?.share === undefined) {
    return 100;
  }
  return Number(center.share);
};

const getShareAmount = (amount: number, center?: CollectionCenter | null) => {
  return (Number(amount || 0) * getCenterShare(center)) / 100;
};

const formatCollectionCenterName = (center?: CollectionCenter | null) => {
  const name = center?.collectionCenterName || "";
  return `${name} (${getCenterShare(center)}%)`;
};

const shareHighlightSx = {
  backgroundColor: "#fff3e0",
  color: "#e65100",
  fontWeight: 700,
  px: 0.75,
  py: 0.25,
  borderRadius: "4px",
  display: "inline-block",
};

const formatCollectionCenterNameCell = (center?: CollectionCenter | null) => {
  const name = center?.collectionCenterName || "";
  const share = getCenterShare(center);
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="span">{name}</Box>
      <Box component="span" sx={shareHighlightSx}>
        ({share}%)
      </Box>
    </Box>
  );
};

const formatShareAmountCell = (amount: number) => (
  <Box component="span" sx={shareHighlightSx}>
    {` ${Number(amount || 0).toLocaleString()}`}
  </Box>
);

const PaymentSummary: React.FC = () => {
  const dispatch = useDispatch();

  const today = new Date().toISOString().split("T")[0];

  const [summaryData, setSummaryData] = useState<PaymentSummaryData[]>([]);
  const [loading, setLoading] = useState(false);

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getLabStateCenterWise`,
        {
          params: {
            fromDate,
            toDate,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setSummaryData(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch payment summary");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Error fetching payment summary data"
      );
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch data when dependencies change
  useEffect(() => {
    fetchSummaryData();
  }, [fromDate, toDate]);

  const handleExcelExport = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    // Calculate totals
    const totals = summaryData.reduce(
      (acc, row) => ({
        patientCount: acc.patientCount + row.patientCount,
        grossAmount: acc.grossAmount + row.grossAmount,
        discount: acc.discount + row.discount,
        paidAmount: acc.paidAmount + row.paidAmount,
        dueAmount: acc.dueAmount + row.dueAmount,
        shareGrossAmount:
          acc.shareGrossAmount + getShareAmount(row.grossAmount, row.collectionCenter),
        sharePaidAmount:
          acc.sharePaidAmount + getShareAmount(row.paidAmount, row.collectionCenter),
        shareDueAmount:
          acc.shareDueAmount + getShareAmount(row.dueAmount, row.collectionCenter),
      }),
      {
        patientCount: 0,
        grossAmount: 0,
        discount: 0,
        paidAmount: 0,
        dueAmount: 0,
        shareGrossAmount: 0,
        sharePaidAmount: 0,
        shareDueAmount: 0,
      }
    );

    const worksheetData = summaryData?.map(row => ({
      "Collection Center": formatCollectionCenterName(row?.collectionCenter),
      Address: row?.collectionCenter?.address,
      "Patient Count": row.patientCount,
      "Gross Amount": row.grossAmount,
      "Share Gross": getShareAmount(row.grossAmount, row.collectionCenter),
      Discount: row.discount,
      "Paid Amount": row.paidAmount,
      "Share Paid": getShareAmount(row.paidAmount, row.collectionCenter),
      "Due Amount": row.dueAmount,
      "Share Due": getShareAmount(row.dueAmount, row.collectionCenter),
    }));

    // Add totals row
    worksheetData.push({
      "Collection Center": "TOTAL",
      Address: "",
      "Patient Count": totals.patientCount,
      "Gross Amount": totals.grossAmount,
      "Share Gross": totals.shareGrossAmount,
      Discount: totals.discount,
      "Paid Amount": totals.paidAmount,
      "Share Paid": totals.sharePaidAmount,
      "Due Amount": totals.dueAmount,
      "Share Due": totals.shareDueAmount,
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);

    // Style the totals row (make it bold)
    const totalRowIndex = worksheetData.length;
    const range = xlsx.utils.decode_range(worksheet["!ref"] || "A1");

    // Apply bold styling to the total row
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = xlsx.utils.encode_cell({
        r: totalRowIndex - 1,
        c: col,
      });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: "s", v: "" };
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "FFFFD700" } }, // Light yellow background
      };
    }

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "PaymentSummary");
    xlsx.writeFile(
      workbook,
      `Payment_Summary_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const handlePrint = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    // Calculate totals
    const totals = summaryData.reduce(
      (acc, row) => ({
        patientCount: acc.patientCount + row.patientCount,
        grossAmount: acc.grossAmount + row.grossAmount,
        discount: acc.discount + row.discount,
        paidAmount: acc.paidAmount + row.paidAmount,
        dueAmount: acc.dueAmount + row.dueAmount,
        shareGrossAmount:
          acc.shareGrossAmount + getShareAmount(row.grossAmount, row.collectionCenter),
        sharePaidAmount:
          acc.sharePaidAmount + getShareAmount(row.paidAmount, row.collectionCenter),
        shareDueAmount:
          acc.shareDueAmount + getShareAmount(row.dueAmount, row.collectionCenter),
      }),
      {
        patientCount: 0,
        grossAmount: 0,
        discount: 0,
        paidAmount: 0,
        dueAmount: 0,
        shareGrossAmount: 0,
        sharePaidAmount: 0,
        shareDueAmount: 0,
      }
    );

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    // Professional header
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("PAYMENT SUMMARY REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 3;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Metadata section
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "COLLECTION CENTER",
        "ADDRESS",
        "PATIENT COUNT",
        "GROSS AMOUNT",
        "SHARE GROSS",
        "DISCOUNT",
        "PAID AMOUNT",
        "SHARE PAID",
        "DUE AMOUNT",
        "SHARE DUE",
      ],
    ];

    const body = summaryData.map(row => [
      formatCollectionCenterName(row?.collectionCenter),
      row?.collectionCenter?.address ?? "",
      row.patientCount?.toString() ?? "0",
      formatCurrency(row.grossAmount ?? 0),
      formatCurrency(getShareAmount(row.grossAmount, row.collectionCenter)),
      formatCurrency(row.discount ?? 0),
      formatCurrency(row.paidAmount ?? 0),
      formatCurrency(getShareAmount(row.paidAmount, row.collectionCenter)),
      formatCurrency(row.dueAmount ?? 0),
      formatCurrency(getShareAmount(row.dueAmount, row.collectionCenter)),
    ]);

    // Add totals row
    body.push([
      "TOTAL",
      "",
      totals.patientCount.toString(),
      formatCurrency(totals.grossAmount),
      formatCurrency(totals.shareGrossAmount),
      formatCurrency(totals.discount),
      formatCurrency(totals.paidAmount),
      formatCurrency(totals.sharePaidAmount),
      formatCurrency(totals.dueAmount),
      formatCurrency(totals.shareDueAmount),
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 5.5,
        halign: "center",
        valign: "middle",
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 5,
        textColor: [40, 40, 40],
        cellPadding: 1.8,
      },
      columnStyles: {
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
        7: { halign: "center" },
        8: { halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function (data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 5.5;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = {
            top: 2.5,
            right: 2,
            bottom: 2.5,
            left: 2,
          };
        }
      },
      didDrawPage: function (data) {
        // Add footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      },
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Calculate totals for display
  const totalPaidAmount = summaryData.reduce(
    (sum, item) => sum + item.paidAmount,
    0
  );

  // Calculate totals for the table
  const tableTotals = summaryData.reduce(
    (acc, row) => ({
      patientCount: acc.patientCount + row.patientCount,
      grossAmount: acc.grossAmount + row.grossAmount,
      discount: acc.discount + row.discount,
      paidAmount: acc.paidAmount + row.paidAmount,
      dueAmount: acc.dueAmount + row.dueAmount,
      shareGrossAmount:
        acc.shareGrossAmount + getShareAmount(row.grossAmount, row.collectionCenter),
      sharePaidAmount:
        acc.sharePaidAmount + getShareAmount(row.paidAmount, row.collectionCenter),
      shareDueAmount:
        acc.shareDueAmount + getShareAmount(row.dueAmount, row.collectionCenter),
    }),
    {
      patientCount: 0,
      grossAmount: 0,
      discount: 0,
      paidAmount: 0,
      dueAmount: 0,
      shareGrossAmount: 0,
      sharePaidAmount: 0,
      shareDueAmount: 0,
    }
  );

  // Prepare data for the DynamicTable component
  const tableData = summaryData?.map(item => [
    formatCollectionCenterNameCell(item?.collectionCenter),
    item?.collectionCenter?.address,
    item.patientCount,
    ` ${item.grossAmount.toLocaleString()}`,
    formatShareAmountCell(getShareAmount(item.grossAmount, item.collectionCenter)),
    ` ${item.discount.toLocaleString()}`,
    ` ${item.paidAmount.toLocaleString()}`,
    formatShareAmountCell(getShareAmount(item.paidAmount, item.collectionCenter)),
    ` ${item.dueAmount.toLocaleString()}`,
    formatShareAmountCell(getShareAmount(item.dueAmount, item.collectionCenter)),
  ]);

  // Add totals row to table data
  if (summaryData.length > 0) {
    tableData.push([
      "TOTAL",
      "",
      tableTotals.patientCount,
      ` ${tableTotals.grossAmount.toLocaleString()}`,
      formatShareAmountCell(tableTotals.shareGrossAmount),
      ` ${tableTotals.discount.toLocaleString()}`,
      ` ${tableTotals.paidAmount.toLocaleString()}`,
      formatShareAmountCell(tableTotals.sharePaidAmount),
      ` ${tableTotals.dueAmount.toLocaleString()}`,
      formatShareAmountCell(tableTotals.shareDueAmount),
    ]);
  }

  // Reusable button styles
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <DateRangeSelector
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={setDateRange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={[
            "Collection Center",
            "Address",
            "Patient Count",
            "Gross Amount",
            "Share Gross",
            "Discount",
            "Paid Amount",
            "Share Paid",
            "Due Amount",
            "Share Due",
          ]}
          data={tableData}
          loading={loading}
          height={"60vh"}
          totalAmount={totalPaidAmount}
        />
      </Box>
    </Box>
  );
};

export default PaymentSummary;
